import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Spinner from 'react-bootstrap/Spinner';
import {
  FaBoxOpen,
  FaChartLine,
  FaCopy,
  FaDollarSign,
  FaExclamationCircle,
  FaInfoCircle,
  FaLink,
  FaMousePointer,
  FaUsers,
} from 'react-icons/fa';
import useUser from '../../hooks/useUser';
import { getAffiliateCodeForApi } from '../../utils/utmTracker';
import affiliateHeroImage from '../../assets/botspot_affiliate_hero.webp';
import './AffiliatePage.css';

const API_URL = process.env.REACT_APP_API_URL;
const AFFILIATE_JOIN_INTENT_KEY = 'botspot_affiliate_join_intent';
const AFFILIATE_DASHBOARD_PATH = '/affiliate/dashboard';
const AFFILIATE_LANDING_PATH = '/affiliate';
const AFFILIATE_HERO_PRELOAD_ID = 'botspot-affiliate-hero-preload';

const money = (cents) => `$${((Number(cents) || 0) / 100).toFixed(2)}`;
const TIMELINE_CHART = { width: 720, height: 220, top: 24, right: 28, bottom: 36, left: 36 };

function getLatestBankPayoutNotice(payout) {
  if (!payout?.id) return null;
  const amount = money(payout.amountCents);
  const status = String(payout.status || '').toLowerCase();
  if (status === 'failed') {
    return {
      tone: 'warning',
      text: `Latest bank payout ${amount} failed. Check payout details or contact support.`,
    };
  }
  if (status === 'paid') {
    return {
      tone: 'success',
      text: `Latest bank payout ${amount} is expected to be available in your bank account.`,
    };
  }
  if (status === 'pending' || status === 'in_transit') {
    return {
      tone: 'success',
      text: `Latest bank payout ${amount} is ${status.replace(/_/g, ' ')}.`,
    };
  }
  return null;
}

function preloadAffiliateHeroImage() {
  if (typeof document === 'undefined' || document.getElementById(AFFILIATE_HERO_PRELOAD_ID)) {
    return;
  }

  const link = document.createElement('link');
  link.id = AFFILIATE_HERO_PRELOAD_ID;
  link.rel = 'preload';
  link.as = 'image';
  link.href = affiliateHeroImage;
  link.type = 'image/webp';
  link.setAttribute('fetchpriority', 'high');
  document.head.appendChild(link);
}

preloadAffiliateHeroImage();

function getCurrentPathname() {
  if (typeof window === 'undefined') return '/affiliate';
  const pathname = typeof window.location?.pathname === 'string' ? window.location.pathname : '/affiliate';
  return pathname.replace(/\/+$/, '') || '/';
}

function getStripeReturnState() {
  if (typeof window === 'undefined') return { returned: false, refreshed: false };
  const search = typeof window.location?.search === 'string' ? window.location.search : '';
  const params = new URLSearchParams(search);
  return {
    returned: params.get('stripe_connect_return') === '1',
    refreshed: params.get('stripe_connect_refresh') === '1',
  };
}

function clearStripeReturnParams() {
  if (typeof window === 'undefined') return;
  const href = typeof window.location?.href === 'string'
    ? window.location.href
    : `${window.location?.origin || 'http://localhost'}${getCurrentPathname()}`;
  const url = new URL(href, window.location?.origin || 'http://localhost');
  url.searchParams.delete('stripe_connect_return');
  url.searchParams.delete('stripe_connect_refresh');
  window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
}

const affiliateBenefits = [
  {
    title: '20% direct referrals',
    text: 'Earn from customers who buy through your link.',
    icon: FaDollarSign,
  },
  {
    title: '5% from invited affiliates',
    text: 'Earn when affiliates you invite bring in paying customers.',
    icon: FaUsers,
  },
  {
    title: 'Lifetime customer credit',
    text: 'Future eligible purchases keep counting for you.',
    icon: FaLink,
  },
  {
    title: 'Tracked payouts',
    text: 'Commissions stay tracked until payout setup is ready.',
    icon: FaChartLine,
  },
];

const affiliateWorkflowSections = [
  {
    label: 'Direct referrals',
    title: 'Earn 20% when your own referral becomes a paying customer.',
    text: 'Share your BotSpot affiliate link with traders, investors, and market-focused audiences. When someone signs up through your link and completes a paid purchase, BotSpot records the customer under your affiliate profile and creates a direct commission from that payment.',
    icon: FaDollarSign,
  },
  {
    label: 'Recruited affiliates',
    title: 'Earn 5% when an affiliate you invited brings in sales.',
    text: 'Your same link can also invite another affiliate. If that person joins the affiliate program through your link, they become connected to you as a recruited affiliate. They still earn their normal direct commission on their own customers, and you earn an additional 5% reward when those customers pay.',
    icon: FaUsers,
  },
  {
    label: 'Lifetime credit',
    title: 'Keep credit for customers you directly refer.',
    text: 'After a customer is attributed to your affiliate link, future eligible purchases from that same customer continue to count toward your affiliate earnings. The first valid affiliate relationship is preserved, so you do not have to keep re-sending a link before every renewal or future purchase.',
    icon: FaLink,
  },
  {
    label: 'Payouts',
    title: 'Commissions are tracked first, then paid after payout setup is ready.',
    text: 'You can share your link before payout setup is complete. Once a commission is created, BotSpot keeps it pending until your payout account is ready, then transfers eligible earnings automatically.',
    icon: FaChartLine,
  },
];

export default function AffiliatePage() {
  const { isAuthenticated, isLoading, getApiToken } = useUser();
  const [currentPath, setCurrentPath] = useState(getCurrentPathname);
  const [profile, setProfile] = useState(null);
  const [totals, setTotals] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [profileReady, setProfileReady] = useState(false);
  const [profileLoadFailed, setProfileLoadFailed] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [creating, setCreating] = useState(false);
  const [startingOnboarding, setStartingOnboarding] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    const syncPath = () => setCurrentPath(getCurrentPathname());
    window.addEventListener('popstate', syncPath);
    return () => window.removeEventListener('popstate', syncPath);
  }, []);

  const navigateToDashboard = useCallback(() => {
    if (typeof window === 'undefined') return;
    if (getCurrentPathname() !== AFFILIATE_DASHBOARD_PATH) {
      window.history.pushState({}, '', AFFILIATE_DASHBOARD_PATH);
    }
    setCurrentPath(AFFILIATE_DASHBOARD_PATH);
  }, []);

  const navigateToAffiliateLanding = useCallback(() => {
    if (typeof window === 'undefined') return;
    if (getCurrentPathname() !== AFFILIATE_LANDING_PATH) {
      window.history.pushState({}, '', AFFILIATE_LANDING_PATH);
    }
    setCurrentPath(AFFILIATE_LANDING_PATH);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setProfile(null);
      setTotals(null);
      setTimeline([]);
      setProfileLoadFailed(false);
      setError('');
      setNotice('');
      setCreating(false);
      setStartingOnboarding(false);
      setProfileReady(false);
      setLoadingProfile(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!notice && !error) return undefined;
    const timer = window.setTimeout(() => {
      setNotice('');
      setError('');
    }, 3500);
    return () => window.clearTimeout(timer);
  }, [notice, error]);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (!API_URL) {
      setError('Affiliate setup is temporarily unavailable.');
      setProfileLoadFailed(true);
      setProfileReady(true);
      navigateToDashboard();
      return;
    }
    let cancelled = false;

    async function loadProfile() {
      setLoadingProfile(true);
      setProfileReady(false);
      setProfileLoadFailed(false);
      setError('');
      try {
        const token = await getApiToken();
        const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await fetch(`${API_URL}/affiliates/me`, {
          headers: authHeaders,
        });
        if (!response.ok) {
          throw new Error('Unable to load affiliate details.');
        }
        const data = await response.json();
        const shouldAutoCreate = window.sessionStorage.getItem(AFFILIATE_JOIN_INTENT_KEY) === 'true';
        if (!data.profile && shouldAutoCreate) {
          setCreating(true);
          const affiliateCode = getAffiliateCodeForApi();
          const createResponse = await fetch(`${API_URL}/affiliates/me`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...authHeaders,
            },
            body: JSON.stringify(affiliateCode ? { affiliateCode } : {}),
          });
          const created = await createResponse.json();
          if (!createResponse.ok) {
            throw new Error(created?.message || 'Unable to create affiliate link.');
          }
          // The create request succeeded, so consume the one-time intent even if
          // a concurrent auth/profile refresh has already cancelled this effect.
          // Leaving it behind can submit the create flow again on the next load.
          window.sessionStorage.removeItem(AFFILIATE_JOIN_INTENT_KEY);
          if (!cancelled) {
            setProfile(created.profile || null);
            setTotals(created.totals || created.profile?.totals || null);
            setTimeline(Array.isArray(created.timeline) ? created.timeline : []);
            setProfileLoadFailed(false);
            setNotice('Link ready.');
            navigateToDashboard();
          }
          return;
        }
        if (!cancelled) {
          let nextProfile = data.profile || null;
          let nextTotals = data.totals || null;
          let nextTimeline = Array.isArray(data.timeline) ? data.timeline : [];
          const stripeReturnState = getStripeReturnState();
          if (nextProfile && stripeReturnState.returned) {
            const syncResponse = await fetch(`${API_URL}/affiliates/me/stripe-account-sync`, {
              method: 'POST',
              headers: authHeaders,
            });
            const synced = await syncResponse.json();
            if (syncResponse.ok) {
              nextProfile = synced.profile || nextProfile;
              nextTotals = synced.totals || nextProfile?.totals || nextTotals;
              nextTimeline = Array.isArray(synced.timeline) ? synced.timeline : nextTimeline;
              setNotice('Payout setup updated.');
            }
            clearStripeReturnParams();
          } else if (stripeReturnState.refreshed) {
            clearStripeReturnParams();
          } else {
            setNotice('');
          }
          setProfile(nextProfile);
          setTotals(nextTotals);
          setTimeline(nextTimeline);
          setProfileLoadFailed(false);
          if (nextProfile) {
            navigateToDashboard();
          } else if (getCurrentPathname() === AFFILIATE_DASHBOARD_PATH) {
            navigateToAffiliateLanding();
          }
        }
      } catch (loadError) {
        if (!cancelled) {
          setProfileLoadFailed(true);
          setError(loadError.message || 'Unable to load affiliate details.');
          navigateToDashboard();
        }
      } finally {
        if (!cancelled) {
          setLoadingProfile(false);
          setProfileReady(true);
          setCreating(false);
        }
      }
    }

    loadProfile();
    return () => {
      cancelled = true;
    };
  }, [getApiToken, isAuthenticated, navigateToAffiliateLanding, navigateToDashboard]);

  const affiliateUrl = useMemo(() => {
    if (!profile?.code || typeof window === 'undefined') return '';
    return `${window.location.origin}/?aff=${encodeURIComponent(profile.code)}`;
  }, [profile?.code]);
  const isAffiliateSuspended = profile?.status === 'suspended';
  const stripeAccountStatus = profile?.stripeAccountStatus || 'not_started';
  const isStripePayoutReady =
    stripeAccountStatus === 'active' &&
    profile?.stripeTransfersEnabled &&
    profile?.stripePayoutsEnabled &&
    profile?.stripeDetailsSubmitted;
  const pendingCommissionCents = totals?.awaitingTransferCommissionCents ?? totals?.pendingCommissionCents ?? 0;
  const transferredCommissionCents = totals?.transferredCommissionCents ?? totals?.paidCommissionCents ?? 0;
  const failedCommissionCents = totals?.transferIssueCommissionCents ?? totals?.failedCommissionCents ?? 0;
  const commissionNeedingTransferCents = pendingCommissionCents + failedCommissionCents;
  const shouldPromptStripePayoutSetup =
    !isStripePayoutReady && !isAffiliateSuspended && commissionNeedingTransferCents > 0;
  const shouldShowTransferFailureAlert =
    isStripePayoutReady && !isAffiliateSuspended && failedCommissionCents > 0;
  const latestBankPayoutNotice = getLatestBankPayoutNotice(profile?.stripeLatestPayout);
  const clicks = totals?.clicks || profile?.clickCount || 0;
  const customers = totals?.customers || 0;
  const timelineChart = useMemo(() => {
    const points = (Array.isArray(timeline) ? timeline : [])
      .map((point) => ({
        label: point?.label || point?.date || '',
        totalReferrals: Number(point?.totalReferrals) || 0,
      }))
      .filter((point) => point.label);
    const hasData = points.some((point) => point.totalReferrals > 0);
    if (!hasData) return { hasData: false, path: '', circles: [], max: 0 };

    const max = Math.max(...points.map((point) => point.totalReferrals), 1);
    const plotWidth = TIMELINE_CHART.width - TIMELINE_CHART.left - TIMELINE_CHART.right;
    const plotHeight = TIMELINE_CHART.height - TIMELINE_CHART.top - TIMELINE_CHART.bottom;
    const circles = points.map((point, index) => {
      const x = TIMELINE_CHART.left + (points.length === 1 ? plotWidth / 2 : (index / (points.length - 1)) * plotWidth);
      const y = TIMELINE_CHART.top + plotHeight - (point.totalReferrals / max) * plotHeight;
      return { ...point, x, y };
    });
    const path = circles
      .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x.toFixed(1)} ${point.y.toFixed(1)}`)
      .join(' ');
    return { hasData: true, path, circles, max };
  }, [timeline]);

  const startAuthSignup = () => {
    window.sessionStorage.setItem(AFFILIATE_JOIN_INTENT_KEY, 'true');
    const params = new URLSearchParams({
      returnTo: AFFILIATE_DASHBOARD_PATH,
      source: 'affiliate_join',
      intent: 'signup',
    });
    window.location.href = `/login?${params.toString()}`;
  };

  const joinAffiliateProgram = async () => {
    setCreating(true);
    setError('');
    setNotice('');
    try {
      if (!API_URL) throw new Error('Affiliate setup is temporarily unavailable.');
      const token = await getApiToken();
      const affiliateCode = getAffiliateCodeForApi();
      const response = await fetch(`${API_URL}/affiliates/me`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(affiliateCode ? { affiliateCode } : {}),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.message || 'Unable to create affiliate link.');
      }
      setProfile(data.profile || null);
      setTotals(data.totals || data.profile?.totals || null);
      setTimeline(Array.isArray(data.timeline) ? data.timeline : []);
      setProfileLoadFailed(false);
      setNotice('Link ready.');
      window.sessionStorage.removeItem(AFFILIATE_JOIN_INTENT_KEY);
      navigateToDashboard();
    } catch (createError) {
      setError(createError.message || 'Unable to create affiliate link.');
    } finally {
      setCreating(false);
    }
  };

  const startStripeOnboarding = async () => {
    setCreating(true);
    setStartingOnboarding(true);
    setError('');
    setNotice('');
    try {
      if (!API_URL) throw new Error('Affiliate setup is temporarily unavailable.');
      const token = await getApiToken();
      const response = await fetch(`${API_URL}/affiliates/me/stripe-onboarding-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.message || 'Unable to create affiliate link.');
      }
      setProfile(data.profile || null);
      setTotals(data.totals || data.profile?.totals || null);
      setTimeline(Array.isArray(data.timeline) ? data.timeline : []);
      setProfileLoadFailed(false);
      setNotice('Opening payout setup.');
      window.sessionStorage.removeItem(AFFILIATE_JOIN_INTENT_KEY);
      if (data.url) {
        window.location.href = data.url;
        return;
      }
    } catch (createError) {
      setError(createError.message || 'Unable to start payout setup.');
    } finally {
      setCreating(false);
      setStartingOnboarding(false);
    }
  };

  const copyAffiliateUrl = async () => {
    if (isAffiliateSuspended || !affiliateUrl || typeof navigator === 'undefined') return;
    try {
      await navigator.clipboard.writeText(affiliateUrl);
      setNotice('Link copied.');
    } catch {
      setError('Unable to copy the affiliate link.');
    }
  };

  const isResolvingAffiliateState = isLoading || loadingProfile || (isAuthenticated && !profileReady);
  const isDashboardRoute = currentPath === AFFILIATE_DASHBOARD_PATH;
  const shouldShowDashboard = Boolean(isDashboardRoute && (profile || profileLoadFailed));
  const shouldShowToast = Boolean((notice || error) && !(profileLoadFailed && !profile));

  const joinButton = isAuthenticated ? (
    <button
      className="affiliate-primary-button"
      type="button"
      onClick={joinAffiliateProgram}
      disabled={creating}
    >
      {creating ? (
        <>
          Creating
          <span className="affiliate-button-spinner" aria-hidden="true" />
        </>
      ) : (
        'Join Affiliate Program'
      )}
    </button>
  ) : (
    <button className="affiliate-primary-button" type="button" onClick={startAuthSignup}>
      Join Affiliate Program
    </button>
  );

  if (isResolvingAffiliateState) {
    return (
      <main className="affiliate-page">
        <div className="affiliate-page-loader" role="status" aria-label="Loading affiliate page">
          <Spinner animation="border" variant="light" />
        </div>
      </main>
    );
  }

  return (
    <main className="affiliate-page">
      <section className={`affiliate-shell ${shouldShowDashboard ? 'affiliate-shell-dashboard' : ''}`}>
        {shouldShowDashboard ? (
          <section className="affiliate-dashboard">
            <div className="affiliate-dashboard-header">
              <div>
                <h2>Dashboard</h2>
                <p>Track your referrals and get rewarded</p>
              </div>
              <button
                className="affiliate-referral-link-button"
                type="button"
                onClick={copyAffiliateUrl}
                disabled={isAffiliateSuspended || !affiliateUrl}
                aria-label="Copy referral link"
              >
                <FaCopy aria-hidden="true" />
                <span>Your referral link</span>
                <FaInfoCircle aria-hidden="true" />
              </button>
            </div>
            {shouldShowToast && (
              <div className="affiliate-toast-stack" aria-live="polite">
                {notice && <div className="affiliate-alert success">{notice}</div>}
                {error && <div className="affiliate-alert error">{error}</div>}
              </div>
            )}
            {!profile ? (
              <div className="affiliate-dashboard-unavailable" role="status">
                <div className="affiliate-dashboard-unavailable-icon" aria-hidden="true">
                  <FaExclamationCircle />
                </div>
                <div>
                  <h3>Unable to load affiliate details.</h3>
                  <p>Check your connection and refresh this page.</p>
                  <button
                    type="button"
                    className="affiliate-unavailable-refresh"
                    onClick={() => window.location.reload()}
                  >
                    Refresh page
                  </button>
                </div>
              </div>
            ) : (
              <>
                {isAffiliateSuspended && (
                  <div className="affiliate-alert warning">
                    Your affiliate link is currently suspended. New affiliate customers will not be tracked.
                  </div>
                )}
                {shouldPromptStripePayoutSetup && (
                  <div className="affiliate-alert warning">
                    {money(commissionNeedingTransferCents)} is waiting. Complete payout setup to receive commissions.
                    <button
                      className="affiliate-secondary-button affiliate-alert-action"
                      type="button"
                      onClick={startStripeOnboarding}
                      disabled={startingOnboarding}
                    >
                      {startingOnboarding ? 'Opening...' : 'Complete setup'}
                    </button>
                  </div>
                )}
                {shouldShowTransferFailureAlert && (
                  <div className="affiliate-alert warning affiliate-dashboard-warning affiliate-transfer-review-alert">
                    <FaExclamationCircle aria-hidden="true" />
                    <span>{money(failedCommissionCents)} transfer needs review. Check your payout details or contact support.</span>
                  </div>
                )}
                {latestBankPayoutNotice && (
                  <div className={`affiliate-alert ${latestBankPayoutNotice.tone} affiliate-dashboard-warning`}>
                    <FaInfoCircle aria-hidden="true" />
                    <span>{latestBankPayoutNotice.text}</span>
                  </div>
                )}
                <div className="affiliate-dashboard-summary">
                  {!isStripePayoutReady && !isAffiliateSuspended && !shouldPromptStripePayoutSetup && (
                    <div className="affiliate-payout-setup-card">
                      <div className="affiliate-action-card-main">
                        <div className="affiliate-action-card-icon" aria-hidden="true">
                          <FaDollarSign />
                        </div>
                        <div>
                          <span>Payout setup</span>
                          <strong>Required before commissions can be paid.</strong>
                        </div>
                      </div>
                      <button
                        className="affiliate-secondary-button"
                        type="button"
                        onClick={startStripeOnboarding}
                        disabled={startingOnboarding}
                      >
                        {startingOnboarding ? 'Opening...' : 'Set up'}
                      </button>
                    </div>
                  )}
                  <div className={`affiliate-link-strip ${isAffiliateSuspended ? 'is-suspended' : ''}`}>
                    <div>
                      <span>Affiliate link</span>
                      <strong>{isAffiliateSuspended ? 'Affiliate link suspended' : affiliateUrl || 'Link unavailable'}</strong>
                    </div>
                    <button
                      type="button"
                      onClick={copyAffiliateUrl}
                      disabled={isAffiliateSuspended || !affiliateUrl}
                      aria-label="Copy affiliate link"
                    >
                      <FaCopy aria-hidden="true" />
                    </button>
                  </div>
                  <div className="affiliate-dashboard-metrics">
                    <div className="affiliate-stat-card affiliate-stat-card-visits">
                      <div className="affiliate-stat-icon" aria-hidden="true"><FaMousePointer /></div>
                      <div>
                        <span>Visits</span>
                        <strong>{clicks}</strong>
                      </div>
                    </div>
                    <div className="affiliate-stat-card affiliate-stat-card-buyers">
                      <div className="affiliate-stat-icon" aria-hidden="true"><FaUsers /></div>
                      <div>
                        <span>Buyers</span>
                        <strong>{customers}</strong>
                      </div>
                    </div>
                    <div className="affiliate-stat-card affiliate-stat-card-pending">
                      <div className="affiliate-stat-icon" aria-hidden="true"><FaDollarSign /></div>
                      <div>
                        <span>Awaiting transfer</span>
                        <strong>{money(pendingCommissionCents)}</strong>
                      </div>
                    </div>
                    <div className="affiliate-stat-card affiliate-stat-card-paid">
                      <div className="affiliate-stat-icon" aria-hidden="true"><FaChartLine /></div>
                      <div>
                        <span>Transferred</span>
                        <strong>{money(transferredCommissionCents)}</strong>
                      </div>
                    </div>
                    <div className="affiliate-stat-card affiliate-stat-card-issues">
                      <div className="affiliate-stat-icon" aria-hidden="true"><FaExclamationCircle /></div>
                      <div>
                        <span>Transfer issues</span>
                        <strong>{money(failedCommissionCents)}</strong>
                      </div>
                    </div>
                  </div>
                  <div className="affiliate-timeline-card">
                    <div className="affiliate-timeline-header">
                      <h3>Total Affiliates Over Time</h3>
                      <FaInfoCircle aria-hidden="true" />
                    </div>
                    <div className="affiliate-timeline-body">
                      {timelineChart.hasData ? (
                        <svg
                          className="affiliate-timeline-chart"
                          viewBox={`0 0 ${TIMELINE_CHART.width} ${TIMELINE_CHART.height}`}
                          role="img"
                          aria-label="Total affiliates over time chart"
                        >
                          <line
                            x1={TIMELINE_CHART.left}
                            y1={TIMELINE_CHART.height - TIMELINE_CHART.bottom}
                            x2={TIMELINE_CHART.width - TIMELINE_CHART.right}
                            y2={TIMELINE_CHART.height - TIMELINE_CHART.bottom}
                          />
                          <line
                            x1={TIMELINE_CHART.left}
                            y1={TIMELINE_CHART.top}
                            x2={TIMELINE_CHART.left}
                            y2={TIMELINE_CHART.height - TIMELINE_CHART.bottom}
                          />
                          <path d={timelineChart.path} />
                          {timelineChart.circles.map((point) => (
                            <g key={`${point.label}-${point.x}`}>
                              <circle cx={point.x} cy={point.y} r="4" />
                              <text x={point.x} y={TIMELINE_CHART.height - 12}>{point.label}</text>
                            </g>
                          ))}
                        </svg>
                      ) : (
                        <div className="affiliate-timeline-empty">
                          <FaBoxOpen aria-hidden="true" />
                          <span>No data to display</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </section>
        ) : (
          <section className="affiliate-landing">
            <div className="affiliate-hero-card">
              <div className="affiliate-hero-copy">
                <span className="affiliate-kicker">
                  <span aria-hidden="true" />
                  BotSpot Affiliate
                </span>
                <h1>
                  <span className="affiliate-title-line affiliate-title-line-primary">
                    Earn Up to <span className="affiliate-title-percent">20%</span>
                  </span>
                  <span className="affiliate-title-line">by sharing</span>
                  <span className="affiliate-title-line affiliate-title-brand">BotSpot</span>
                </h1>
                <p>
                  Earn 20% from customers you refer directly, plus 5% when affiliates you invite bring in paying customers.
                </p>
                <div className="affiliate-hero-actions">
                  {joinButton}
                </div>
                {error && <div className="affiliate-alert error">{error}</div>}
              </div>
            </div>
            <div className="affiliate-benefit-grid">
              {affiliateBenefits.map((benefit) => {
                const BenefitIcon = benefit.icon;
                return (
                  <article className="affiliate-benefit-card" key={benefit.title}>
                    <BenefitIcon aria-hidden="true" />
                    <h2>{benefit.title}</h2>
                    <p>{benefit.text}</p>
                  </article>
                );
              })}
            </div>
            <div className="affiliate-steps-card">
              <h2>How it works</h2>
              <p className="affiliate-steps-intro">
                The program has two earning paths: direct customer referrals and invited-affiliate rewards.
              </p>
              <div className="affiliate-workflow">
                {affiliateWorkflowSections.map((section) => {
                  const WorkflowIcon = section.icon;
                  return (
                    <article className="affiliate-workflow-row" key={section.label}>
                      <div className="affiliate-workflow-heading">
                        <WorkflowIcon aria-hidden="true" />
                        <span>{section.label}</span>
                      </div>
                      <h3>{section.title}</h3>
                      <p>{section.text}</p>
                    </article>
                  );
                })}
              </div>
            </div>
          </section>
        )}
      </section>
    </main>
  );
}
